import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { WarningAlert } from 'components/blocks/warning-alert';
import { Checkbox } from 'components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import {
  useGetOpenConnectorCatalog,
  useGetOpenConnectorServiceActions,
  useGetToolById,
  useSaveOpenConnectorTool,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type {
  IOpenConnectorCatalogService,
  ToolStatus,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { cn } from 'lib/utils';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { getOpenConnectorConfig, getToolEditSubject, getToolVisibility } from './tool-detail-utils';
import { APPROVAL_TO_VALUE, HumanApprovalField, toApprovalOption, type HumanApprovalOption } from './human-approval';

const CATALOG_RESULTS_CAP = 30;
const ACTION_RESULTS_CAP = 100;

const SERVICE_SUGGESTIONS = ['gmail', 'github', 'slack', 'googlecalendar', 'notion'];

const serviceMatches = (services: string[], service: string) =>
  services.some((item) => item === service);

type EditOpenConnectorToolModalProps = {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: {
    id: string;
    name: string;
    description: string;
    status?: string;
    visibility?: string | null;
    created_by?: string;
    CreatedBy?: string;
  };
};

type OpenConnectorFormData = {
  toolName: string;
  description: string;
  visibility: ResourceVisibility;
  status: boolean;
  humanApproval: HumanApprovalOption;
};

function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="space-y-0.5">
        <h3 className="text-[13px] font-medium leading-6">{title}</h3>
        {description ? (
          <p className="text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function ServiceCatalogRow({
  entry,
  isSelected,
  onSelect,
}: {
  entry: IOpenConnectorCatalogService;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const logo = entry.iconUrl;

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-2.5 py-2 text-left transition-colors',
        isSelected
          ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
          : 'border-transparent hover:border-border hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          className="h-8 w-8 shrink-0 rounded-md border bg-background object-contain p-0.5"
          loading="lazy"
        />
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
          {entry.displayName.slice(0, 2)}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium leading-5">{entry.displayName}</span>
        <span className="block truncate text-xs text-muted-foreground">{entry.service}</span>
      </span>
      {isSelected ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" aria-hidden />
        </span>
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-full border border-border" aria-hidden />
      )}
    </button>
  );
}

function ServiceActionPicker({
  service,
  selectedActionIds,
  onToggleAction,
  onSelectAllActions,
  defaultExpanded = false,
  disabled = false,
}: {
  service: string;
  selectedActionIds: string[];
  onToggleAction: (actionId: string, checked: boolean) => void;
  onSelectAllActions: (service: string, actionIds: string[]) => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [search, setSearch] = useState('');
  const actionsQuery = useGetOpenConnectorServiceActions(service, { enabled: expanded });

  const selectedForService = useMemo(
    () => selectedActionIds.filter((id) => id.startsWith(`${service}.`)),
    [selectedActionIds, service]
  );

  const filtered = useMemo(() => {
    const items = actionsQuery.data?.actions ?? [];
    const term = search.trim().toLowerCase();
    const matching = term
      ? items.filter(
        (item) =>
          item.id.toLowerCase().includes(term) ||
          item.name.toLowerCase().includes(term) ||
          (item.description ?? '').toLowerCase().includes(term)
      )
      : items;
    return matching.slice(0, ACTION_RESULTS_CAP);
  }, [actionsQuery.data, search]);

  const allActionIds = useMemo(
    () => (actionsQuery.data?.actions ?? []).map((item) => item.id),
    [actionsQuery.data]
  );
  const allSelected =
    allActionIds.length > 0 && allActionIds.every((id) => selectedActionIds.includes(id));
  const someSelected = selectedForService.length > 0 && !allSelected;

  const statusLabel =
    selectedForService.length > 0
      ? t('ACTIONS_SELECTED', {
        defaultValue: '{{count}} action(s) selected',
        count: selectedForService.length,
      })
      : t('ALL_ACTIONS', { defaultValue: 'All actions' });

  const handleSelectAllChange = (checked: boolean) => {
    if (disabled) return;
    onSelectAllActions(service, checked ? allActionIds : []);
  };

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="overflow-hidden rounded-lg border bg-background"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40"
        >
          <span className="flex min-w-0 items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-[13px] font-medium">{service}</span>
          </span>
          <Badge
            variant={selectedForService.length > 0 ? 'secondary' : 'outline'}
            className="shrink-0 text-[10px] font-normal dark:text-[#ffffff] text-[#000000]"
          >
            {statusLabel}
          </Badge>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 overflow-visible border-t bg-muted/20 px-3 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('SEARCH_ACTIONS', { defaultValue: 'Search actions...' })}
            disabled={disabled}
            className="h-8 border-border/60 bg-background pl-8 text-[13px]"
          />
        </div>
        {actionsQuery.isLoading && (
          <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('LOADING_ACTIONS', { defaultValue: 'Loading actions...' })}
          </div>
        )}
        {actionsQuery.isError && (
          <p className="py-2 text-[13px] text-destructive">
            {t('FAILED_TO_LOAD_ACTIONS', {
              defaultValue: 'Failed to load actions for this service.',
            })}
          </p>
        )}
        {!actionsQuery.isLoading && !actionsQuery.isError && filtered.length === 0 && (
          <p className="py-2 text-[13px] text-muted-foreground">
            {t('NO_ACTIONS_FOUND', { defaultValue: 'No actions found.' })}
          </p>
        )}
        {allActionIds.length > 0 && !actionsQuery.isLoading && !actionsQuery.isError && (
          <label
            className={cn(
              'flex items-center gap-2.5 rounded-md px-1 py-1.5',
              disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
            )}
          >
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={(value) => handleSelectAllChange(value === true)}
              disabled={disabled}
            />
            <span className="text-[13px] font-medium leading-5">
              {t('SELECT_ALL', { defaultValue: 'Select all' })}
            </span>
          </label>
        )}
        {filtered.length > 0 && (
          <div className="space-y-0.5 rounded-md border border-border bg-background dark:bg-card/50 p-1 pb-3">
            {filtered.map((item) => {
              const checked = selectedActionIds.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={cn(
                    'flex items-start gap-2.5 rounded-md px-2 py-2 transition-colors',
                    disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-muted/60 dark:hover:bg-muted/40',
                    checked && 'bg-primary/10 dark:bg-primary/20'
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onToggleAction(item.id, value === true)}
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium leading-5 text-foreground">
                      {item.name || item.id}
                    </span>
                    {item.description ? (
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function EditOpenConnectorToolModal({
  mode,
  open,
  onOpenChange,
  tool,
}: EditOpenConnectorToolModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const { mutateAsync: saveOpenConnectorTool, isPending } = useSaveOpenConnectorTool();

  const isEdit = mode === 'edit';
  const { data: toolDetails } = useGetToolById(
    { toolId: tool?.id || '', projectKey },
    { enabled: open && isEdit && !!tool?.id && !!projectKey }
  );
  const isOwner = useIsOwner(getToolEditSubject(toolDetails, tool));
  const canEdit = !isEdit || isOwner;

  const [services, setServices] = useState<string[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [limitInput, setLimitInput] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [showServicePicker, setShowServicePicker] = useState(true);

  const selectedService = services[0] ?? null;

  const catalogQuery = useGetOpenConnectorCatalog(catalogSearch.trim() || undefined, { enabled: open });

  const catalogByService = useMemo(() => {
    const map = new Map<string, IOpenConnectorCatalogService>();
    for (const entry of catalogQuery.data?.services ?? []) {
      map.set(entry.service, entry);
    }
    return map;
  }, [catalogQuery.data]);

  const catalogResults = useMemo(() => {
    const catalog = catalogQuery.data?.services ?? [];
    const term = catalogSearch.trim().toLowerCase();
    const matching = term
      ? catalog.filter(
        (entry) =>
          entry.service.toLowerCase().includes(term) ||
          entry.displayName.toLowerCase().includes(term)
      )
      : catalog;

    const sorted = [...matching].sort((a, b) => {
      const aSelected = serviceMatches(services, a.service) ? 0 : 1;
      const bSelected = serviceMatches(services, b.service) ? 0 : 1;
      return aSelected - bSelected;
    });

    return sorted.slice(0, CATALOG_RESULTS_CAP);
  }, [catalogQuery.data, catalogSearch, services]);

  const selectedCatalogEntry = selectedService
    ? catalogByService.get(selectedService)
    : undefined;
  const selectedServiceLogo = selectedCatalogEntry?.iconUrl ?? null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<OpenConnectorFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      status: false,
      humanApproval: 'auto',
    },
  });

  useEffect(() => {
    if (!open) return;

    if (!isEdit) {
      reset({
        toolName: '',
        description: '',
        visibility: DEFAULT_RESOURCE_VISIBILITY,
        status: false,
        humanApproval: 'auto',
      });
      setServices([]);
      setSelectedActionIds([]);
      setLimitInput('');
      setCatalogSearch('');
      setShowServicePicker(true);
      return;
    }

    const detail = toolDetails?.tool ?? toolDetails?.data;
    const config = getOpenConnectorConfig(detail ?? (toolDetails as never));
    reset({
      toolName: detail?.name || tool?.name || '',
      description: detail?.description || tool?.description || '',
      visibility: getToolVisibility(detail ?? tool),
      status: (detail?.status || tool?.status) === 'active',
      humanApproval: toApprovalOption(detail?.RequiresApproval ?? toolDetails?.RequiresApproval),
    });
    setServices(config.services.slice(0, 1));
    setSelectedActionIds(config.actions ?? []);
    setLimitInput(config.limit != null ? String(config.limit) : '');
    setShowServicePicker(config.services.length === 0);
  }, [open, isEdit, toolDetails, tool, reset]);

  const toolName = watch('toolName');
  const description = watch('description');
  const hasActionSelection = selectedActionIds.length > 0;
  const canSave =
    Boolean(toolName?.trim()) &&
    Boolean(description?.trim()) &&
    services.length > 0;

  const syncServices = (nextServices: string[]) => {
    const next = nextServices.slice(0, 1);
    setServices(next);
    setSelectedActionIds((current) =>
      current.filter((id) => next.some((service) => id.startsWith(`${service}.`)))
    );
    setShowServicePicker(next.length === 0);
  };

  const handleSelectService = (service: string) => {
    const normalized = service.trim().toLowerCase();
    if (!normalized) return;

    if (serviceMatches(services, normalized)) {
      syncServices([]);
      return;
    }

    syncServices([normalized]);
    setShowServicePicker(false);
    setCatalogSearch('');
  };

  const handleToggleAction = (actionId: string, checked: boolean) => {
    setSelectedActionIds((current) =>
      checked ? Array.from(new Set([...current, actionId])) : current.filter((id) => id !== actionId)
    );
  };

  const handleSelectAllActions = (service: string, actionIds: string[]) => {
    setSelectedActionIds((current) => {
      const withoutService = current.filter((id) => !id.startsWith(`${service}.`));
      return actionIds.length > 0 ? [...withoutService, ...actionIds] : withoutService;
    });
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      setServices([]);
      setSelectedActionIds([]);
      setLimitInput('');
      setCatalogSearch('');
      setShowServicePicker(true);
    }
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: OpenConnectorFormData) => {
    if (!canEdit) return;

    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: t('MISSING_PROJECT_KEY', { defaultValue: 'Missing project key' }),
        description: t('UNABLE_TO_SAVE_OPEN_CONNECTOR_TOOL', {
          defaultValue: 'Unable to save the open connector tool for this tenant.',
        }),
      });
      return;
    }

    if (services.length === 0) {
      toast({
        variant: 'destructive',
        title: t('CONNECTOR_SERVICE_REQUIRED', { defaultValue: 'Service required' }),
        description: t('ADD_ONE_OPEN_CONNECTOR_SERVICE', {
          defaultValue: 'Select a service, such as Gmail or GitHub.',
        }),
      });
      return;
    }

    const parsedLimit = limitInput.trim() ? Number(limitInput) : null;
    if (!hasActionSelection && parsedLimit != null && (!Number.isInteger(parsedLimit) || parsedLimit < 1)) {
      toast({
        variant: 'destructive',
        title: t('INVALID_LIMIT', { defaultValue: 'Invalid limit' }),
        description: t('LIMIT_MUST_BE_POSITIVE', {
          defaultValue: 'Limit must be a positive whole number.',
        }),
      });
      return;
    }

    try {
      const response = await saveOpenConnectorTool({
        ...(isEdit && tool?.id ? { id: tool.id } : {}),
        name: data.toolName.trim(),
        description: data.description.trim(),
        services,
        actions: hasActionSelection ? selectedActionIds : null,
        limit: hasActionSelection ? null : parsedLimit ?? 10,
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: false,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
        ...(isEdit ? { status: (data.status ? 'active' : 'inactive') as ToolStatus } : {}),
      });

      if (!response.is_success) {
        throw new Error(
          response.message ||
          response.detail ||
          t('FAILED_TO_SAVE_OPEN_CONNECTOR_TOOL', { defaultValue: 'Failed to save open connector tool' })
        );
      }

      toast({
        variant: 'success',
        title: isEdit
          ? t('OPEN_CONNECTOR_TOOL_UPDATED', { defaultValue: 'Open connector tool updated' })
          : t('OPEN_CONNECTOR_TOOL_CREATED', { defaultValue: 'Open connector tool created' }),
        description: response.detail || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['tools'] });
      handleClose(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isEdit
          ? t('UPDATE_FAILED', { defaultValue: 'Update failed' })
          : t('CREATE_FAILED', { defaultValue: 'Create failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_SAVE_OPEN_CONNECTOR_TOOL', { defaultValue: 'Failed to save open connector tool.' }),
      });
    }
  };

  let saveLabel = t('CREATE', { defaultValue: 'Create' });
  if (isPending) saveLabel = t('SAVING', { defaultValue: 'Saving...' });
  else if (isEdit) saveLabel = t('SAVE', { defaultValue: 'Save' });

  const showCatalog =
    catalogQuery.isSuccess && (catalogQuery.data?.services?.length ?? 0) > 0;
  const showCatalogPicker = showServicePicker || !selectedService;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-[640px] flex-col overflow-hidden rounded-lg border-0 p-0 shadow-xl sm:max-h-[min(90vh,900px)]">
        <DialogHeader className="shrink-0 space-y-1 border-b px-5 pb-4 pt-5 sm:px-6">
          <DialogTitle className="pr-8 text-lg font-semibold leading-snug sm:text-xl">
            {!isEdit
              ? t('ADD_OPEN_CONNECTOR_TOOL', { defaultValue: 'Add open connector tool' })
              : canEdit
                ? t('EDIT_OPEN_CONNECTOR_TOOL', { defaultValue: 'Edit open connector tool' })
                : t('VIEW_OPEN_CONNECTOR_TOOL', { defaultValue: 'View open connector tool' })}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            {t('OPEN_CONNECTOR_TOOL_MODAL_DESCRIPTION', {
              defaultValue:
                'Connect third-party services so your agent can act on user accounts. Users sign in when an action needs access.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form className="flex min-h-0 flex-1 flex-col overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 pb-8 pt-5 sm:px-6">
            {!canEdit ? (
              <WarningAlert>
                {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
                  'You are not the owner of this tool.'}
              </WarningAlert>
            ) : null}

            <FormSection title={`${t('TOOL_NAME') || 'Tool name'} *`}>
              <Input
                id="openConnectorToolName"
                placeholder={t('ENTER_NAME') || 'Enter name'}
                {...register('toolName', {
                  required: t('TOOL_NAME_REQUIRED') || 'Tool name is required',
                  setValueAs: (value) => value.trim(),
                })}
                disabled={!canEdit || isPending}
                className={getToolFormInputClassName(!!errors.toolName)}
              />
              {errors.toolName ? (
                <p className="text-[13px] text-destructive">{errors.toolName.message}</p>
              ) : null}
            </FormSection>

            <Controller
              name="visibility"
              control={control}
              render={({ field }) => (
                <VisibilityField
                  value={field.value}
                  onValueChange={field.onChange}
                  idPrefix="open-connector-tool-visibility"
                  disabled={!canEdit || isPending}
                />
              )}
            />

            <FormSection title={`${t('DESCRIPTION') || 'Description'} *`}>
              <Textarea
                id="openConnectorDescription"
                rows={3}
                placeholder={t('CONNECTOR_DESCRIPTION_PLACEHOLDER', {
                  defaultValue: 'What should this tool help the agent do?',
                })}
                {...register('description', {
                  required: t('DESCRIPTION_REQUIRED') || 'Description is required',
                  setValueAs: (value) => value.trim(),
                })}
                disabled={!canEdit || isPending}
                className={getToolFormTextareaClassName(!!errors.description)}
              />
              {errors.description ? (
                <p className="text-[13px] text-destructive">{errors.description.message}</p>
              ) : null}
            </FormSection>

            <HumanApprovalField
              control={control}
              name="humanApproval"
              id="openConnectorHumanApproval"
              toolType="open_connector"
              disabled={!canEdit || isPending}
            />

            <FormSection
              title={`${t('CONNECTOR_APP', { defaultValue: 'App' })} *`}
              description={
                selectedService && !showServicePicker
                  ? t('CONNECTOR_APP_SELECTED_HINT', {
                    defaultValue: 'This tool uses one connected app. Change it below if needed.',
                  })
                  : t('CONNECTOR_APP_HINT', {
                    defaultValue: 'Pick one app from the list below.',
                  })
              }
            >
              {selectedService && !showServicePicker ? (
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
                  {selectedServiceLogo ? (
                    <img
                      src={selectedServiceLogo}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md border bg-background object-contain p-0.5"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-semibold uppercase">
                      {(selectedCatalogEntry?.displayName ?? selectedService).slice(0, 2)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium leading-5">
                      {selectedCatalogEntry?.displayName ?? selectedService}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{selectedService}</p>
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={() => setShowServicePicker(true)}
                    >
                      {t('CHANGE_APP', { defaultValue: 'Change app' })}
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {showCatalogPicker && showCatalog ? (
                <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={catalogSearch}
                      onChange={(event) => setCatalogSearch(event.target.value)}
                      placeholder={t('SEARCH_CONNECTOR_APPS', {
                        defaultValue: 'Search apps (Gmail, GitHub, Slack…)',
                      })}
                      disabled={!canEdit || isPending}
                      className="h-9 border-border/60 bg-background pl-8 text-[13px]"
                    />
                  </div>
                  <div className="space-y-1">
                    {catalogResults.map((entry) => (
                      <ServiceCatalogRow
                        key={entry.service}
                        entry={entry}
                        isSelected={serviceMatches(services, entry.service)}
                        onSelect={() => handleSelectService(entry.service)}
                      />
                    ))}
                    {catalogResults.length === 0 ? (
                      <p className="px-2 py-4 text-center text-[13px] text-muted-foreground">
                        {t('NO_APPS_FOUND', { defaultValue: 'No apps found.' })}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : showCatalogPicker && catalogQuery.isLoading ? (
                <div className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('LOADING_APPS', { defaultValue: 'Loading apps…' })}
                </div>
              ) : showCatalogPicker ? (
                <div className="flex flex-wrap gap-1.5">
                  {SERVICE_SUGGESTIONS.map((suggestion) => (
                    <Badge
                      key={suggestion}
                      variant={serviceMatches(services, suggestion) ? 'default' : 'outline'}
                      className="cursor-pointer text-xs text-[#ffffff]"
                      onClick={() => handleSelectService(suggestion)}
                    >
                      {suggestion}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </FormSection>

            {selectedService ? (
              <FormSection
                title={t('ACTION_ALLOWLIST', { defaultValue: 'Action allowlist' })}
                description={t('ACTION_ALLOWLIST_HINT', {
                  defaultValue:
                    'Optional. Choose specific actions, or leave empty to use the most common ones automatically.',
                })}
              >
                <ServiceActionPicker
                  service={selectedService}
                  selectedActionIds={selectedActionIds}
                  onToggleAction={handleToggleAction}
                  onSelectAllActions={handleSelectAllActions}
                  defaultExpanded
                  disabled={!canEdit}
                />
              </FormSection>
            ) : null}

          </div>

          <DialogFooter className="shrink-0 gap-2 border-t px-5 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={isPending}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={!canEdit || !canSave || isPending}>
              {saveLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
